import { useEffect, useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

export default function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(0);
  const perPage = 3;

  useEffect(() => { api.get("/reviews").then((r) => setReviews(r.data)); }, []);

  const totalPages = Math.ceil(reviews.length / perPage);
  const slice = reviews.slice(page * perPage, page * perPage + perPage);
  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 5;

  return (
    <section className="py-24 md:py-32 bg-[#0A0A0B] relative" data-testid="reviews-section">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Kundenzufriedenheit</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white mt-3">
              Was unsere Kunden sagen.
            </h2>
          </div>
          <div className="flex items-center gap-4 bg-[#141417] border border-white/10 rounded-2xl px-5 py-4">
            <div>
              <div className="font-display text-4xl font-bold text-white numeric">{avg.toFixed(1)}</div>
              <div className="text-xs text-zinc-500">{reviews.length}+ Bewertungen</div>
            </div>
            <div className="flex">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {slice.map((r, i) => (
            <div key={i} className="card-dark p-7" data-testid={`review-card-${i}`}>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((j) => (
                  <Star key={j} className={`w-4 h-4 ${j <= r.rating ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`} />
                ))}
              </div>
              <p className="text-zinc-300 mt-4 leading-relaxed text-[15px] line-clamp-6">&ldquo;{r.text}&rdquo;</p>
              <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm text-white">{r.author}</div>
                  <div className="text-xs text-zinc-500">{r.date}</div>
                </div>
                <div className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                  ✓ Google verifiziert
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center disabled:opacity-30 hover:border-white/30 text-white" data-testid="reviews-prev">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="text-sm text-zinc-500 px-3">{page + 1} / {totalPages}</div>
            <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page === totalPages - 1} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center disabled:opacity-30 hover:border-white/30 text-white" data-testid="reviews-next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
